/**
 * Shared layout for auth pages (login, register, forgot password, reset password, email verification).
 * Styled according to the consentry-v3 design system (Inter font, tokens, radial grid background, clean card container).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  badge?: string;
  children: ReactNode;
}

export function AuthLayout({
  title,
  subtitle,
  badge = 'v1.4.2 · GA',
  children,
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111] flex flex-col justify-between p-4 sm:p-6 bg-sg relative font-sans antialiased">
      {/* Top Navigation Bar */}
      <header className="max-w-6xl w-full mx-auto flex items-center justify-between py-4 px-2">
        <Link
          to="/"
          className="flex items-center gap-2.5 font-semibold text-base tracking-tight hover:opacity-90 transition-opacity"
        >
          <span className="w-8 h-8 rounded-lg bg-blue flex items-center justify-center text-white shadow-sm shrink-0">
            <svg
              className="w-4.5 h-4.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </span>
          <span className="font-bold text-lg text-ink">Consentry</span>
        </Link>

        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center mono text-[11px] text-muted border border-line rounded-full px-3 py-1 bg-surface select-none shadow-xs">
            {badge}
          </span>
          <Link
            to="/"
            className="text-xs font-medium text-sub hover:text-ink transition-colors px-2.5 py-1.5 rounded-lg hover:bg-slate-100/60"
          >
            Back to Home →
          </Link>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex items-center justify-center py-8 px-2">
        <div className="w-full max-w-md bg-white border border-line rounded-3xl p-7 sm:p-9 shadow-cardhover relative overflow-hidden transition-all">
          {/* Accent border top */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue via-indigo-500 to-emerald" />

          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 text-[11px] font-medium text-sub border border-line bg-surface px-3 py-1 rounded-full mb-3 mono">
              <span className="w-1.5 h-1.5 rounded-full bg-ok" />
              DPDP Act, 2023 Compliant
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">
              {title}
            </h1>
            <p className="mt-2 text-xs sm:text-sm text-sub font-medium leading-relaxed max-w-sm mx-auto">
              {subtitle}
            </p>
          </div>

          <div>{children}</div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-md w-full mx-auto text-center py-4">
        <p className="text-[11.5px] text-muted font-medium">
          Consentry · Digital Personal Data Protection (DPDP) Act Infrastructure
        </p>
      </footer>
    </div>
  );
}
